import { z } from "zod";
/**
 * Passio REST API schemas for food detail responses.
 *
 * @see https://passio.gitbook.io/nutrition-ai/rest-api/usage-guide/food-details-routes
 * @see https://passio.gitbook.io/nutrition-ai/rest-api/data-essentials/models-and-usage
 * @see https://passio.gitbook.io/nutrition-ai/fundamentals/api-sdk-interoperability/nutrition-data-model-mapping
 */
/** `PassioMeasurement` — portion weight in grams. */
export const PassioMeasurementSchema = z.object({
    unit: z.string(),
    value: z.number(),
});
/** `PassioMeasuredPortion` — a named serving with weight and suggested quantity. */
export const PassioMeasuredPortionSchema = z.object({
    quantity: z.number(),
    name: z.string(),
    weight: PassioMeasurementSchema,
    /** Defaults to 1 when absent — @see Passio nutritional data model docs. */
    suggestedQuantity: z.array(z.number()).optional(),
});
/** `PassioNutrient` — nutrient metadata (name, unit, shortName). */
export const PassioNutrientSchema = z.object({
    name: z.string().optional(),
    unit: z.string().optional(),
    shortName: z.string().optional(),
});
/** `PassioFoodNutrient` — per-100g nutrient amount from REST. */
export const PassioFoodNutrientSchema = z.object({
    id: z.number().optional(),
    nutrient: PassioNutrientSchema,
    amount: z.number(),
});
/**
 * SDK `PassioNutrients` object shape (pre-scaled values keyed by shortName).
 * Not returned by REST directly; accepted when hydrating SDK-equivalent JSON.
 */
export const PassioSdkNutrientValueSchema = z.object({
    value: z.number().nullable().optional(),
    unit: z.string().nullable().optional(),
});
export const PassioSdkNutrientsObjectSchema = z.record(z.string(), PassioSdkNutrientValueSchema);
/** REST nutrients array or SDK nutrients object. */
export const PassioNutrientsFieldSchema = z.union([
    z.array(PassioFoodNutrientSchema),
    PassioSdkNutrientsObjectSchema,
]);
/**
 * SDK `PassioFoodAmount` shape — selected unit/quantity and alternate serving units.
 * REST maps servings to `portions[]` instead.
 */
export const PassioSdkServingUnitSchema = z.object({
    unitName: z.string(),
    value: z.number().optional(),
});
export const PassioSdkFoodAmountSchema = z.object({
    selectedQuantity: z.number().optional(),
    selectedUnit: z.string().optional(),
    servingUnits: z.array(PassioSdkServingUnitSchema).optional(),
});
/** `PassioFood` — ingredient / single food item. */
export const PassioFoodSchema = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    nutrients: PassioNutrientsFieldSchema.optional(),
    portions: z.array(PassioMeasuredPortionSchema).optional(),
    refCode: z.string().optional(),
    iconId: z.string().optional(),
    metadata: z
        .object({
        /** SDK `nutrientsReference()` — pre-scaled nutrient map stored in ingredient metadata. */
        nutrients_reference: PassioSdkNutrientsObjectSchema.optional(),
        nutrientsReference: PassioSdkNutrientsObjectSchema.optional(),
    })
        .optional(),
});
/** `PassioFoodResult` — top-level search result detail. */
export const PassioFoodResultSchema = z.object({
    refCode: z.string().optional(),
    displayName: z.string().optional(),
    iconId: z.string().optional(),
    type: z.string().optional(),
    internalId: z.string().optional(),
    internalName: z.string().optional(),
    name: z.string().optional(),
    portions: z.array(PassioMeasuredPortionSchema).optional(),
    nutrients: PassioNutrientsFieldSchema.optional(),
    ingredients: z.array(PassioFoodSchema).optional(),
    /** SDK-only; REST uses `portions[]` for serving info. */
    amount: PassioSdkFoodAmountSchema.optional(),
});
/** `PassioFoodResults` — response from `GET /napi/food/search/result/refCode/{refCode}`. */
export const PassioFoodResultsSchema = z.object({
    results: z.array(PassioFoodResultSchema).min(1),
    alternatives: z.array(z.unknown()).optional(),
});
export const PassioFoodDetailRestPayloadSchema = z.union([
    PassioFoodResultsSchema.transform(payload => payload.results[0]),
    PassioFoodResultSchema,
]);
//# sourceMappingURL=passioFoodDetailRestSchema.js.map